import Model, { attr, belongsTo, type AsyncBelongsTo } from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type IntlService from 'ember-intl/services/intl';
import {
  DateTime,
  Duration,
  type DateTimeInput,
} from 'scaleflow/utils/date-time';
import type InitiativeModel from './initiative';

export const abbreviatedTimeFromNow = (date: DateTimeInput) => {
  const diffInSeconds = DateTime.now().diff(DateTime.from(date), 'seconds');
  const duration = Duration.of(diffInSeconds, 'seconds');

  if (duration.asSeconds() < 60) {
    return `${Math.floor(duration.asSeconds())}s`;
  } else if (duration.asMinutes() < 60) {
    return `${Math.floor(duration.asMinutes())}m`;
  } else if (duration.asHours() < 24) {
    return `${Math.floor(duration.asHours())}h`;
  } else if (duration.asDays() < 7) {
    return `${Math.floor(duration.asDays())}d`;
  } else if (duration.asWeeks() < 4) {
    return `${Math.floor(duration.asWeeks())}w`;
  } else if (duration.asMonths() < 12) {
    return `${Math.floor(duration.asMonths())}mo`;
  } else {
    return `${Math.floor(duration.asYears())}y`;
  }
};

/**
 * Maintain the order in which the key values have been defined in STATUS_KEYS, this determines sorting order while viewing
 *
 */
export const STATUS_KEYS = {
  DELAYED: 'DELAYED',
  AT_RISK: 'AT_RISK',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
  ON_TRACK: 'ON_TRACK',
};

export default class InitiativeUpdateModel extends Model {
  declare [Type]: 'initiative-update';
  @service declare intl: IntlService;

  @attr('string') declare initiative_id: string;
  @attr('string') declare status: string;
  @attr declare text_delta: unknown;

  @belongsTo('initiative', {
    async: true,
    inverse: 'initiative-updates',
  })
  declare initiative: AsyncBelongsTo<InitiativeModel>;
  @attr declare created_at: string;
  @attr declare updated_by: { id: string };
  @attr declare updated_at: string;
  @attr declare reported_health: unknown;

  get updatedByPerson() {
    return this.store.peekRecord('person', this.updated_by.id);
  }

  get statusLabel() {
    return this.status
      ? this.intl.t(`_initiatives.updates_${this.status.toLowerCase()}`)
      : '';
  }

  get humanReadableUpdatedAt() {
    return abbreviatedTimeFromNow(this.updated_at);
  }
}
